import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  ApiCollectionResponse,
  ApiSingleResponse,
} from '../interfaces/responses/api-response';
import {
  Pack,
  PackLevelDiscount,
  PackLevelPrice,
  StudentFeatureModule,
  SubjectPackPrice,
  UpdatePackDto,
  UpsertLevelDiscount,
  UpsertLevelPrice,
  UpsertSubjectPrice,
} from '../interfaces/pack';

/**
 * Packs commerciaux et grille tarifaire par matière.
 *
 * <p>Le montant d'une inscription est la somme des prix des matières
 * choisies : cette grille est donc la source de vérité des tarifs, et le
 * serveur recalcule toujours à partir d'elle.</p>
 */
@Injectable({ providedIn: 'root' })
export class PackAdminService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.BASE_URL}/admin/packs`;

  getAll(): Observable<ApiCollectionResponse<Pack>> {
    return this.http.get<ApiCollectionResponse<Pack>>(this.baseUrl);
  }

  update(id: number, dto: UpdatePackDto): Observable<ApiSingleResponse<Pack>> {
    return this.http.put<ApiSingleResponse<Pack>>(`${this.baseUrl}/${id}`, dto);
  }

  getSubjectPrices(
    packId: number,
  ): Observable<ApiCollectionResponse<SubjectPackPrice>> {
    return this.http.get<ApiCollectionResponse<SubjectPackPrice>>(
      `${this.baseUrl}/${packId}/subject-prices`,
    );
  }

  /**
   * Remplace **en bloc** la grille du pack : toute ligne absente du corps est
   * supprimée côté serveur. L'écran envoie donc la grille complète.
   */
  replaceSubjectPrices(
    packId: number,
    prices: UpsertSubjectPrice[],
  ): Observable<ApiCollectionResponse<SubjectPackPrice>> {
    return this.http.put<ApiCollectionResponse<SubjectPackPrice>>(
      `${this.baseUrl}/${packId}/subject-prices`,
      prices,
    );
  }

  /**
   * Remplace en bloc les prix affichés du pack, par niveau et périodicité —
   * ce sont eux que montre le site vitrine.
   */
  replaceLevelPrices(
    packId: number,
    prices: UpsertLevelPrice[],
  ): Observable<ApiCollectionResponse<PackLevelPrice>> {
    return this.http.put<ApiCollectionResponse<PackLevelPrice>>(
      `${this.baseUrl}/${packId}/level-prices`,
      prices,
    );
  }

  /**
   * Remplace en bloc les remises par classe du pack.
   *
   * <p>Une classe absente du corps n'a plus de remise : c'est ainsi qu'on en
   * retire une, l'endpoint ne connaissant pas la suppression ligne à
   * ligne.</p>
   */
  replaceLevelDiscounts(
    packId: number,
    discounts: UpsertLevelDiscount[],
  ): Observable<ApiCollectionResponse<PackLevelDiscount>> {
    return this.http.put<ApiCollectionResponse<PackLevelDiscount>>(
      `${this.baseUrl}/${packId}/level-discounts`,
      discounts,
    );
  }

  /** Les fonctionnalités de l'espace élève qu'un pack peut ouvrir, par module. */
  getStudentFeatures(): Observable<ApiCollectionResponse<StudentFeatureModule>> {
    return this.http.get<ApiCollectionResponse<StudentFeatureModule>>(
      `${this.baseUrl}/student-features`,
    );
  }

  /** Les clés des fonctionnalités qu'ouvre le pack. */
  getFeatures(packId: number): Observable<ApiCollectionResponse<string>> {
    return this.http.get<ApiCollectionResponse<string>>(`${this.baseUrl}/${packId}/features`);
  }

  /**
   * Remplace en bloc les fonctionnalités du pack : une clé absente n'est plus
   * incluse. Les élèves du pack voient le changement à leur requête suivante.
   */
  replaceFeatures(packId: number, features: string[]): Observable<ApiCollectionResponse<string>> {
    return this.http.put<ApiCollectionResponse<string>>(
      `${this.baseUrl}/${packId}/features`,
      { features },
    );
  }
}
